import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

/**
 * Converts the db ISO string date into an array of string values local event display time.
 * @param iso 
 * @returns 
 */
export const getFormStartFromDateTime = (iso: string): string[] => {

  const isoDate: Date = new Date(iso);

  const dateDateTimeFormat = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne', weekday: 'short', day: 'numeric', hourCycle: 'h12', dayPeriod: 'narrow', hour: 'numeric', minute: "numeric", 
  });

  const parts = dateDateTimeFormat.formatToParts(isoDate);

  //[ 'Fri', '13th', '8', '27', 'in the morning' ]
  const partValues: string[] = parts.map(p => p.value);  

  const day: string = partValues[0];
  const date: string = partValues[2] === `1` ? '1st' : partValues[2] === `2` ? '2nd' : partValues[2] === `3` ? '3rd' : partValues[2] + 'th';
  const hour: string = partValues[4];
  const min: string = partValues[6];
  const ampm: string = partValues[8].endsWith('morning') ? 'am' : 'pm';
  return [day,date,hour,min,ampm]
}

/**
 * Converts the local event display time into an ISO string for DB entry 
 * @param day in display format (4th or 5th)
 * @param time in display format (9am, 2pm)
 * @returns an iso date for db entry
 */
export const getDateTimeFromFormStart = (day: string, time: string): string => {

  // process the day
  const numberDayArray: RegExpMatchArray | null = day.match(/[0-9]+/)
  console.log(numberDayArray)
  // [ '4', index: 0, input: '4th', groups: undefined ]
  const numberDayString = numberDayArray?.at(0)

  const numberDay = parseInt(numberDayString as string);
  console.log(numberDay)

  // process the time (9 am)
  const numberHourArray: RegExpMatchArray | null = time.match(/([0-9]*):([0-9]{2})/)
  // [ '9', index: 0, input: '9:30am', groups: undefined ]
  const numberHourString = numberHourArray?.at(1)
  let numberHour = parseInt(numberHourString as string);
  numberHour = (time.includes('pm')) ? numberHour + 12 : numberHour;
   
  const numberMinString = numberHourArray?.at(2)
  let numberMin = parseInt(numberMinString as string);

  const localDate: Date = new Date();
  localDate.setFullYear(2026, 3, numberDay);
  localDate.setHours(numberHour, numberMin, 0, 0);
  console.log(localDate)
  
  return localDate.toISOString()

}

export const times = [
  {itemValue: '9:00am'},{itemValue: '9:30am'},{itemValue: '10:00am'},{itemValue: '10:30am'},{itemValue: '11:00am'},{itemValue: '11:30am'},
  {itemValue: '12:00pm'},{itemValue: '12:30pm'},{itemValue: '1:00pm'},{itemValue: '1:30pm'},{itemValue: '2:00pm'},{itemValue: '2:30pm'},{itemValue: '3:00pm'},{itemValue: '3:30pm'},
  {itemValue: '4:00pm'},{itemValue: '4:30pm'},{itemValue: '5:00pm'},{itemValue: '5:30pm'},{itemValue: '6:00pm'},{itemValue: '6:30pm'}]

export const sortByStart = (whatsons: Whatson[], direction: 'asc' | 'desc') : Whatson[] => {
  const allStartDateTimesSet: boolean = whatsons.every(e => e.startDateTime);
  if (allStartDateTimesSet) {
    const w_copy: Whatson[] = whatsons.map(w => structuredClone(w));
    if (direction === 'asc') {
      return w_copy.sort((a,b) => new Date(a.startDateTime as string).getDate() - new Date(b.startDateTime as string).getDate());
    } else {
      return w_copy.sort((a,b) => new Date(b.startDateTime as string).getDate() - new Date(a.startDateTime as string).getDate());
    }
  } else {
    throw new Error(`one or more of the Whatsons have not got a startDateTime set`);
  }
}

const filterByLocation = (whatsons: Whatson[], location: 'Port Melbourne' | 'St Kilda') : Whatson[] => {
  return whatsons.filter(e => e.location === location)
}

const filterByDay = (whatsons: Whatson[], day: 'Sat' | 'Sun') : Whatson[] => {
  return whatsons.filter(e => e.day === day)
}

const emptyWhatson = { } as Whatson

export interface Whatson {
  id: string
  title: string
  author: string
  created: string
  updated: string
  vendor: string
  location: string
  startDateTime?: string // DB representation (ISO8601) form to ease sorting
  day: string
  start: string
  minAge: string
}
export interface WhatsonState {
  whatson: Whatson
  whatsons: Whatson[]
  status: 'idle' | 'pending' | 'succeeded' | 'failed'
  sorting: 'none' | 'asc' | 'desc'
  dayFiltering: 'none' | 'Sat' | 'Sun'
  locFiltering: 'none' | 'St Kilda' | 'Port Melbourne'
  error: null | string
}

export interface WhatsonApiPayload {
  accessToken: string
  id: null | string
  whatson: null | Whatson
}

let initialState: WhatsonState = {
  whatson: emptyWhatson,
  whatsons: [],
  status: 'idle',
  sorting: 'none',
  dayFiltering: 'none',
  locFiltering: 'none',
  error: null
} as unknown as WhatsonState;


export const fetchWhatsons = createAsyncThunk<Whatson[], void, { readonly rejectValue: { readonly errorMessage: string } }>(
  'whatsons/fetch',
  async (arg, thunkAPI) => {
    return await fetch(
      '/api/whatsons/',
      {
          method: 'GET',
      })

      .then(response => {
          if (response.status === 200) {
              return response.json();
          } else {
              throw new Error(`Unexpected response from server (code ${response.status}).`);
          }
      })

      .catch((error) => {
          console.error(error);
          return thunkAPI.rejectWithValue({ errorMessage: error.message });
      });
  }
);

// WhatsonApiPayload contains the id of the whatson to fetch and the accesstoken
export const fetchWhatson = createAsyncThunk<Whatson, WhatsonApiPayload, { readonly rejectValue: { readonly errorMessage: string } }>(
  'whatson/fetch',
  async (arg, thunkAPI) => {
    return await fetch(
      `/api/whatsons/${arg.id}`,
      {
          method: 'GET',
      })

      .then(response => {
          if (response.status === 200) {
              return response.json();
          } else {
              throw new Error(`Unexpected response from server (code ${response.status}).`);
          }
      })

      .catch((error) => {
          console.error(error);
          return thunkAPI.rejectWithValue({ errorMessage: error.message });
      });
  }
);

export const createWhatson = createAsyncThunk<Whatson, WhatsonApiPayload, { rejectValue: { readonly errorMessage: string, readonly whatson: WhatsonApiPayload } }>(
  'whatsons/create',

  async (arg, thunkAPI) => {

    if (!arg.whatson) {
      throw new Error(`No event in request.`);
    }

    arg.whatson.startDateTime = getDateTimeFromFormStart(arg.whatson.day, arg.whatson.start);

    return await fetch(
      '/api/whatsons/',
      {
        method: 'POST',
        body: JSON.stringify(arg.whatson),
        ...(arg.accessToken ? {headers: { Authorization: `Basic ${arg.accessToken}`}} : {})
      })

      .then(response => {
        if (response.status === 200) {
            return response.json();
        } else {
            throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })

      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message, whatson: arg });
      });
  }
);

export const editWhatson = createAsyncThunk<Whatson, WhatsonApiPayload, { rejectValue: { readonly errorMessage: string, readonly whatsonApiPayload: WhatsonApiPayload } }>(
  'whatsons/edit',

  async (arg, thunkAPI) => {

    if (!arg.whatson) {
      throw new Error(`No event in request.`);
    }

    arg.whatson.startDateTime = getDateTimeFromFormStart(arg.whatson.day, arg.whatson.start);

    return await fetch(
      '/api/whatsons/',
      {
        method: 'PATCH',
        body: JSON.stringify(arg.whatson),
        ...(arg.accessToken ? {headers: { Authorization: `Basic ${arg.accessToken}`}} : {})
      })

      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })

      .catch((error) => {
        console.error(error);
        return thunkAPI.rejectWithValue({ errorMessage: error.message, whatsonApiPayload: arg });
      });
  }
);
/**
  const fetchUserById = createAsyncThunk<
  // Return type of the payload creator
  MyData,
  // First argument to the payload creator
  number,
  {
    // Optional fields for defining thunkApi field types
    dispatch: AppDispatch
    state: State
    extra: {
      jwt: string
    }
  }
>('users/fetchById', async (userId, thunkApi) => {
  const response = await fetch(`https://reqres.in/api/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${thunkApi.extra.jwt}`,
    },
  })
  return (await response.json()) as MyData
})
 */
export const deleteWhatson = createAsyncThunk<string, WhatsonApiPayload, { rejectValue: { readonly errorMessage: string } }>(
  'whatsons/delete',

  async (arg, thunkApi) => {

    return await fetch(
      `/api/whatsons/${arg.id}`,
      {
        method: 'DELETE',
        body: JSON.stringify(arg.whatson),
        ...(arg.accessToken ? {headers: { Authorization: `Basic ${arg.accessToken}`}} : {})
      })

      .then(response => {
        if (response.status === 200) {
          return '' + response.body;
        } else {
          throw new Error(`Unexpected response from server (code ${response.status}).`);
        }
      })

      .catch((error) => {
        console.error(error);
        return thunkApi.rejectWithValue({ errorMessage: error.message });
      });
  }
);


export const whatsonSlice = createSlice({
  name: 'whatsonstate', // not sure what this 'name' is for. For the moment, I'll just call it the same as the reference in store.ts
  initialState,
  reducers: {
    reset: () => initialState,
    clearReset: (state) => {
      state = initialState;
    },
    setStatus: (state, action: {type: string, payload: 'idle' | 'pending' | 'succeeded' | 'failed'}) => {
      state.status = action.payload;
    },
    filterByDay: (state, action: {type: string, payload: {whatsons: Whatson[], day: 'Sat' | 'Sun'}}) => {
      state.whatsons = filterByDay(action.payload.whatsons, action.payload.day);
      state.dayFiltering = action.payload.day;
    },
    filterByLocation: (state, action: {type: string, payload: {whatsons: Whatson[], location: 'Port Melbourne' | 'St Kilda'}}) => {
      state.whatsons = filterByLocation(action.payload.whatsons, action.payload.location);
      state.locFiltering = action.payload.location;
    },
    sortByStart: (state, action: {type: string, payload: {whatsons: Whatson[], direction: 'asc' | 'desc'}}) => {
      state.whatsons = sortByStart(action.payload.whatsons, action.payload.direction);
      state.sorting = action.payload.direction;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWhatsons.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchWhatsons.fulfilled, (state, action) => {
        let whatsons: Whatson[] = action.payload;
        if (state.dayFiltering !== 'none') { whatsons = filterByDay(whatsons, state.dayFiltering) };
        if (state.locFiltering !== 'none') { whatsons = filterByLocation(whatsons, state.locFiltering) };
        if (state.sorting !== 'none') { whatsons = sortByStart(whatsons, state.sorting) };
        state.whatsons = whatsons; 
        state.error = '';
        state.status = 'succeeded';
      })
      .addCase(fetchWhatsons.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(fetchWhatson.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchWhatson.fulfilled, (state, action) => {
        state.whatson = action.payload;
        state.error = '';
        state.status = 'succeeded';
      })
      .addCase(fetchWhatson.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(createWhatson.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(createWhatson.fulfilled, (state, action) => {
        reset();
        state.status = 'succeeded';
      })
      .addCase(createWhatson.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(editWhatson.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(editWhatson.fulfilled, (state, action) => {

        reset();
        state.status = 'succeeded';
      })
      .addCase(editWhatson.rejected, (state, action) => {
        console.log(action.payload?.errorMessage)
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })

      .addCase(deleteWhatson.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(deleteWhatson.fulfilled, (state, action) => {
        state.whatson = {} as Whatson;
        if (state.whatsons.length > 0) {
          state.whatsons.splice(state.whatsons.findIndex(e => e.id === action.payload) , 1)
        }
        state.error = '';
        state.status = 'succeeded';
      })
      .addCase(deleteWhatson.rejected, (state, action) => {
        if (action.payload !== undefined) {
          state.error = action.payload.errorMessage;
        }
        state.status = 'failed';
      })
;
},
        
});

export const {reset, clearReset, setStatus} = whatsonSlice.actions
export const selectAllWhatsons = (state: RootState) => state.whatsonstate.whatsons

export const selectWhatsonById = (state: RootState, whatsonId: string) =>
  state.whatsonstate.whatsons.find(evt => evt.id === whatsonId)

export const selectWhatsonStatus = (state: RootState) => state.whatsonstate.status
export const selectWhatsonError = (state: RootState) => state.whatsonstate.error

export default whatsonSlice.reducer;